import { useState } from "react";
import axios from "axios";
import { Download, Loader2 } from "lucide-react";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_LABEL = {
  daily: "Daily Lottery",
  lvm: "LVM",
  coupons: "Coupons",
  sp: "Store Paperwork",
};

/**
 * Quick page-level Excel export dropdown.
 *
 * Props:
 *  - section:      one of 'daily' | 'lvm' | 'coupons' | 'sp'
 *  - currentDate:  YYYY-MM-DD string OR a Date object (defaults to today)
 *  - testIdPrefix: optional prefix for data-testid
 *  - size:         shadcn Button size (default "sm")
 */
export function ExportButton({ section, currentDate, testIdPrefix = "export", size = "sm" }) {
  const [loading, setLoading] = useState(false);

  const today = (() => {
    if (!currentDate) return new Date();
    if (currentDate instanceof Date) return currentDate;
    try {
      return new Date(currentDate + "T00:00:00");
    } catch {
      return new Date();
    }
  })();

  const ranges = [
    { key: "today", label: "Just this date", start: today, end: today },
    { key: "week", label: "This Week", start: startOfWeek(today, { weekStartsOn: 1 }), end: endOfWeek(today, { weekStartsOn: 1 }) },
    { key: "month", label: "This Month", start: startOfMonth(today), end: endOfMonth(today) },
    { key: "year", label: "This Year", start: startOfYear(today), end: endOfYear(today) },
  ];

  const run = async (start, end) => {
    setLoading(true);
    const s = format(start, "yyyy-MM-dd");
    const e = format(end, "yyyy-MM-dd");
    try {
      const res = await axios.get(`${API}/export/workbook`, {
        params: { start: s, end: e, sections: section },
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${SECTION_LABEL[section].replace(/\s+/g, "")}_${s}_to_${e}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${SECTION_LABEL[section]} (${s} → ${e})`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Export failed";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={size}
          className="btn-3d"
          disabled={loading}
          title="Export to Excel"
          data-testid={`${testIdPrefix}-btn`}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-1" />
          )}
          <span className="text-xs">{loading ? "Exporting..." : "Excel"}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          Export {SECTION_LABEL[section]}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {ranges.map((r) => (
          <DropdownMenuItem
            key={r.key}
            onClick={() => run(r.start, r.end)}
            data-testid={`${testIdPrefix}-${r.key}`}
            className="cursor-pointer font-mono text-xs"
          >
            <Download className="w-3.5 h-3.5 mr-2" />
            <span className="flex-1">{r.label}</span>
            <span className="text-[10px] text-muted-foreground">
              {format(r.start, "MMM d")}
              {r.key !== "today" && ` – ${format(r.end, "MMM d")}`}
            </span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild data-testid={`${testIdPrefix}-custom`}>
          <a
            href="/exports"
            className="cursor-pointer font-mono text-xs flex items-center"
          >
            <Download className="w-3.5 h-3.5 mr-2" />
            <span className="flex-1">Custom range / Multi-section…</span>
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ExportButton;
